// CouponFloat.tsx
import React, { useState, useEffect } from 'react'
import { Gift, X, Copy, Clock } from 'lucide-react'
import { useTranslation } from '../hooks/useTranslation'
import { useToast } from './ToastProvider'

const TIMER_MINUTES = 15
const STORAGE_KEY = 'coupon_float_ends_at'
const STORAGE_KEY_COPIED = 'coupon_float_copied'

interface Coupon {
  code: string
  value: number
  type: string
}

interface Props {
  coupons: Coupon[]
}

function getInitialState(): { endsAt: number; copied: boolean } {
  try {
    if (sessionStorage.getItem(STORAGE_KEY_COPIED) === '1') {
      return { endsAt: 0, copied: true }
    }
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      const end = parseInt(stored, 10)
      if (end > Date.now()) return { endsAt: end, copied: false }
    }
  } catch {}
  const end = Date.now() + TIMER_MINUTES * 60 * 1000
  sessionStorage.setItem(STORAGE_KEY, String(end))
  return { endsAt: end, copied: false }
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return '0:00'
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const CouponFloat: React.FC<Props> = ({ coupons }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [{ endsAt, copied }, setTimerState] = useState(getInitialState)
  const [, setTick] = useState(0)
  const remaining = copied ? 0 : Math.max(0, endsAt - Date.now())
  const { t } = useTranslation()
  const { showToast } = useToast()

  // تحديث العدّ كل ثانية + إعادة التشغيل عند الانتهاء (ما لم يتم النسخ)
  useEffect(() => {
    if (copied) return
    const id = setInterval(() => {
      const now = Date.now()
      const left = endsAt - now
      if (left <= 0) {
        const newEnd = now + TIMER_MINUTES * 60 * 1000
        sessionStorage.setItem(STORAGE_KEY, String(newEnd))
        setTimerState({ endsAt: newEnd, copied: false })
      }
      setTick((n) => n + 1)
    }, 1000)
    return () => clearInterval(id)
  }, [endsAt, copied])

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      sessionStorage.setItem(STORAGE_KEY_COPIED, '1')
      setTimerState((s) => ({ ...s, copied: true }))
      showToast({ type: 'success', title: t('couponFloat.copied'), message: code })
    } catch {
      showToast({ type: 'error', title: t('common.error') || 'Error', message: '' })
    }
  }

  if (!coupons || coupons.length === 0) return null

  const tooltipLabel = t('couponFloat.tooltip')

  return (
    <div className="fixed bottom-4 left-4 md:bottom-6 md:left-6 z-50">
      <div className="relative">
        {/* Pulse Animation */}
        <div className="absolute inset-0 rounded-full bg-gold animate-ping opacity-75"></div>
        <div className="absolute inset-0 rounded-full bg-gold animate-pulse"></div>

        {/* Main Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative bg-gradient-to-r from-gold via-gold-light to-gold hover:opacity-90 text-bg p-3 md:p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          aria-label={tooltipLabel}
          title={tooltipLabel}
        >
          <Gift size={24} />
        </button>

        {/* Tooltip (سحابي) */}
        {!isOpen && (
          <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-surface border border-border text-text text-xs px-3 py-2 rounded-lg shadow-lg opacity-0 hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none" title={tooltipLabel}>
            {tooltipLabel}
            <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-2 h-2 bg-surface border-l border-b border-border rotate-45"></div>
          </div>
        )}
      </div>

      {/* Coupon Panel - ظاهر على الموبايل والشاشات الكبيرة */}
      {isOpen && (
        <div className="absolute bottom-20 left-0 w-80 max-w-[calc(100vw-2rem)] md:max-w-sm">
          <div className="glass-card dark:glass-card-dark p-4 rounded-2xl shadow-2xl bg-surface/90 border border-border backdrop-blur-md">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-10 h-10 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center">
                  <Gift size={20} className="text-gold" />
                </div>
                <div>
                  <h4 className="font-semibold text-text text-sm">
                    {t('couponFloat.title')}
                  </h4>
                  <p className="text-xs text-gold">{t('couponFloat.subtitle')}</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-text-muted hover:text-text transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* عداد تنازلي — يتكرر حتى نسخ الكوبون */}
            <div className="mb-4 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-gold/10 border border-gold/30">
              <Clock size={18} className="text-gold shrink-0" />
              <span className="text-sm font-medium text-gold-light">
                {copied
                  ? t('couponFloat.timerUsed')
                  : `${t('couponFloat.timerLabel')} ${formatRemaining(remaining)}`}
              </span>
            </div>

            {/* Coupon List */}
            <div className="mb-4">
              {coupons.map((c) => (
                <div
                  key={c.code}
                  className="text-sm text-text bg-surface-soft border border-border p-3 rounded-lg mb-2 flex justify-between items-center gap-2"
                >
                  <span className="font-mono truncate">{c.code}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold text-gold">{c.value}%</span>
                    <button
                      type="button"
                      onClick={() => handleCopyCode(c.code)}
                      className="p-1.5 rounded-md text-text-muted hover:text-gold hover:bg-gold/10 transition-colors"
                      title={t('couponFloat.copy')}
                      aria-label={t('couponFloat.copy')}
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Info */}
            <div className="mt-3 text-center">
              <p className="text-xs text-text-muted">
                {t('couponFloat.footer')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CouponFloat